import type { SubjectIcon } from '@shkola/design';
import type { ReactNode } from 'react';
import {
  ALL_FACES,
  EventArt,
  FaceAvatar,
  MoodArt,
  type MoodKind,
  SubjectArt,
  TaskArt,
  type TaskArtKind,
} from './LineArt';
import { type Grad, grad } from './ShapesArt';
import { EVENT_GRAD } from './ShapesDay';

/*
 * The whole set of pictures of «Форми 2» on one sheet — on the preview only (`?sim=art`): each on a card
 * of the colour it lives on (a lesson of its subject, a kind of task, a kind of event), as on the home.
 */

/** Each subject's picture and the colour of its lessons. */
const SUBJECTS: ReadonlyArray<[SubjectIcon, Grad]> = [
  ['Sigma', 'sky'],
  ['Triangle', 'sky'],
  ['Atom', 'lavender'],
  ['Telescope', 'lavender'],
  ['FlaskConical', 'aqua'],
  ['Leaf', 'mint'],
  ['Sprout', 'mint'],
  ['Earth', 'aqua'],
  ['Map', 'apricot'],
  ['Landmark', 'apricot'],
  ['Library', 'pink'],
  ['BookOpen', 'pink'],
  ['BookMarked', 'sky'],
  ['Languages', 'coral'],
  ['PenLine', 'butter'],
  ['Monitor', 'aqua'],
  ['Music', 'lavender'],
  ['Palette', 'pink'],
  ['Hammer', 'apricot'],
  ['Shield', 'sky'],
  ['HeartPulse', 'mint'],
  ['Scale', 'butter'],
  ['Dumbbell', 'apricot'],
];
const TASKS: ReadonlyArray<[TaskArtKind, Grad]> = [
  ['overdue', 'coral'],
  ['soon', 'cobalt'],
  ['tests', 'lavender'],
  ['rework', 'butter'],
  ['ack', 'pink'],
  ['catchUp', 'mint'],
  ['done', 'lime'],
];
const EVENTS = ['trip', 'exam', 'celebration', 'meeting', 'holiday', 'other'] as const;
const MOODS: ReadonlyArray<MoodKind> = ['great', 'good', 'okay', 'sad', 'angry', 'anxious'];

function Tile({ tone, size, children }: { tone: Grad; size: number; children: ReactNode }) {
  return (
    <span
      className="flex items-center justify-center rounded-[22px]"
      style={{ width: size, height: size, backgroundImage: grad(tone) }}
    >
      {children}
    </span>
  );
}

export function ArtSheet() {
  return (
    <div className="flex flex-col gap-8 rounded-[28px] bg-white p-6 md:p-8">
      <div className="flex flex-wrap gap-3">
        {SUBJECTS.map(([icon, tone]) => (
          <Tile key={icon} tone={tone} size={96}>
            <SubjectArt icon={icon} tone={tone} size={60} />
          </Tile>
        ))}
      </div>
      <div className="flex flex-wrap gap-3">
        {TASKS.map(([kind, tone]) => (
          <Tile key={kind} tone={tone} size={128}>
            <TaskArt kind={kind} tone={tone} size={88} />
          </Tile>
        ))}
      </div>
      <div className="flex flex-wrap gap-3">
        {EVENTS.map((kind) => (
          <Tile key={kind} tone={EVENT_GRAD[kind]} size={112}>
            <EventArt kind={kind} tone={EVENT_GRAD[kind]} size={72} />
          </Tile>
        ))}
      </div>
      <div className="flex flex-wrap gap-4">
        {MOODS.map((m) => (
          <MoodArt key={m} mood={m} size={56} />
        ))}
      </div>
      <div className="flex flex-wrap gap-4">
        {ALL_FACES.map((f) => (
          <FaceAvatar key={f} face={f} initials="КО" size={96} />
        ))}
      </div>
    </div>
  );
}
